import _ from 'lodash'
import md5 from 'md5'
import { ajax } from 'rxjs/ajax'
import {
  _ONPREM,
  ADMIN_CLUSTER_URL_NAME,
  FAWKES_APP_ID,
  FAWKES_APP_IDS,
  MGMT_MANAGE_ROUTE,
  MGMT_OVERVIEW_ROUTE,
  ONPREM,
  REQUEST_TIMEOUT,
  TSG_ID_NONE,
} from '../../framework/constants'
import { getDatadogRum } from '../../framework/externals'
import history from '../../framework/history'
import { jwtDecode as jwt_decode } from '../../framework/jwt'
import { getFrameworkServiceUrl } from '../../framework/states'
import { stateRef } from '../../framework/store'
import {
  clearCurrentTsgId,
  getCurrentTsgId,
  getTsgToken,
  getUserManager,
  hasTsgSupport,
} from '../../framework/tsg'
import { dedupPromise, noop, tryParseErrorJson, setTimeout, clearTimeout } from '../../framework/utils'
import { __error, __info, __warn } from '../../init/debug'
import fetch from '../../libs/fetch'
import { globalAvail, tsgAccessAvail } from '../common/utils'

import type { AjaxResponse } from 'rxjs/ajax'
import type { FawkesAvailFunc, NavItem } from '../../framework/microAppTypes'
import type { AnyState, AuthState, ConfigLocation, FeatureInfo, TokenInfo } from '../../framework/stateTypes'
import type { TsgInstances } from '../../framework/indexedArrays'

export * from '../../framework/loadingMask'
export { jwt_decode }

type FetchPromiseOption = {
  signal: AbortController['signal']
  tsg_id?: string
}

// const JWT_IAT_TOLERANCE = 60000 // 60s

const validUserPreferenceAttributes = ['insights', 'explore', 'config']

export const getAdminClusterURL = (): string | undefined => getFrameworkServiceUrl(ADMIN_CLUSTER_URL_NAME)

export const getLoginServiceURL = (serviceUrl: string) => ({
  SERVICE_URL: `${getAdminClusterURL()}/${serviceUrl}`
})

export const clearUserSessionForLogout = () => {
  if (getCurrentTsgId() === TSG_ID_NONE) {
    clearCurrentTsgId()
    return
  }
  __info('clear fawkes session info and clear session storage')
  const sub = sessionStorage.getItem('currentTenantId') || localStorage.getItem('currentTenantId')
  if (sub) {
    localStorage.removeItem(`token-${sub}`)
    localStorage.removeItem(`token-exp-${sub}`)
    localStorage.removeItem('currentTenantId')
  }
  clearCurrentTsgId()
  sessionStorage.clear()
}

export const isTokenValid = ({ exp, iat }: { exp: number, iat: number }) => {
  const now = Date.now()
  return exp && iat &&
    now < exp * 1000
  // now + JWT_IAT_TOLERANCE > iat * 1000 // allow 60s local time diff
}

/**
 * @deprecated
 */
export const doLogin = () => {
  console.error('non-tsg fawkes doLogin is no longer supported')
}

/**
 * @deprecated
 */
export const doLogout = () => {
  console.error('non-tsg fawkes doLogout is no longer supported')
}

export function getCSRFToken() {
  const now = Date.now()
  return `${now}:${md5(`${now}:`)}`
}

export function getMd5Salt() {
  return ''
}

export function getAuthHeaders(): Record<string, string> {
  const access_token = stateRef.current.auth?.access_token
  return {
    // 'x-csrf-token': getCSRFToken(), // no longer needed
    'x-auth-jwt': access_token || '',
  }
}

export const isFawkesAppId = (appId: string) => FAWKES_APP_IDS.includes(appId)
export const getFawkesInst = (instances: TsgInstances | undefined) => {
  if (instances?.length) {
    for (const appId of FAWKES_APP_IDS) {
      const inst = instances.get(appId)
      if (inst) { // already handled the hidden case when indexing
        return inst
      }
    }
  }
  return undefined
}
export const hasRunningFawkesInst = (instances: TsgInstances | undefined) => {
  if (instances?.isFawkesRunning) {
    return true
  }
  if (instances?.length) {
    return FAWKES_APP_IDS.some(appId => instances.isRunningStatus(appId))
  }
  return undefined
}
export const isPanoramaManagedTenant = (auth: AuthState = stateRef.current.auth): boolean => {
  const fawkesInst = getFawkesInst(auth.instances)
  if (!fawkesInst) {
    return false
  }
  if (fawkesInst.extra?.platform) {
    return fawkesInst.extra?.platform === ONPREM
  }
  return fawkesInst.tenant_id.endsWith(_ONPREM)
}

export const isMigrationStateEligibleForFawkesUI = (): boolean => {
  const status = stateRef.current.main?.migrationStatus
  const hasStatus = [
    'LOAD_CFG_DONE',
    'LICENSE_TRANSFER_DONE',
    'LICENSE_TRANSFER_FAIL',
    'MIGRATION_FAILED',
    'COMMIT_CFG_FAILED'
  ].includes(status)

  return hasStatus
}

export const getParam = (arg: string) => {
  const params = new URLSearchParams(window.location.search)
  if (params.has(arg)) {
    return params.get(arg) || undefined
  }
  return undefined
}

export const updateSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  if (token && token !== 'null') {
    localStorage.setItem(`token-${sub}`, token)
  }
  else {
    localStorage.deleteItem(`token-${sub}`)
  }
  if (exp) {
    const expStr = String(exp)
    localStorage.setItem(`token-exp-${sub}`, expStr)
    sessionStorage.setItem(`token-exp-${sub}`, expStr)
  }
  else {
    localStorage.deleteItem(`token-exp-${sub}`)
    sessionStorage.deleteItem(`token-exp-${sub}`)
  }
}

export const saveSession = ({ sub, token, exp }: { sub: string, token: string, exp: number }) => {
  sessionStorage.setItem('currentTenantId', sub)
  localStorage.setItem('currentTenantId', sub)
  updateSession({ sub, token, exp })
  startSessionMonitor()
}

export const unsetSession = () => { // does not really clear the jwt
  sessionStorage.removeItem('currentTenantId')
  localStorage.removeItem('currentTenantId')
}

export const getSession = (sub?: string): string | undefined => {
  const { tsg_id, fawkes_tenant_id, access_token, access_token_decoded } = stateRef.current.auth || {}
  if (access_token && (!sub || sub === fawkes_tenant_id)) {
    if (access_token_decoded?.exp && access_token_decoded.exp * 1000 > Date.now()) {
      return access_token
    }
    console.error('access access_token from auth and its expired', access_token_decoded?.exp)
    getTsgToken(tsg_id).catch(noop) // try renew
    return undefined
  }
  const tenantId = sub || sessionStorage.getItem('currentTenantId')
  const token = tenantId && localStorage.getItem(`token-${tenantId}`)
  return (token !== 'null' && token) || undefined
}

let sessionTimer: number | undefined
export const startSessionMonitor = () => {
  if (!hasTsgSupport()) {
    return
  }
  if (sessionTimer) {
    clearTimeout(sessionTimer)
  }
  const INTERVAL = 10000 // 10s
  const setTimer = () => {
    sessionTimer = setTimeout(() => {
      const { access_token_decoded } = stateRef.current.auth || {}
      if (access_token_decoded?.tsg_id && +access_token_decoded.exp && access_token_decoded.exp * 1000 < Date.now() + INTERVAL) {
        const userMgr = getUserManager(access_token_decoded.tsg_id)
        if (userMgr?.settings.automaticSilentRenew) {
          __warn('found user session expiring soon without successful renew, try renewing it')
          userMgr.signinSilent().then(setTimer, (err) => {
            __error('failed to renew user session, will retry again after expired', err)
            setTimeout(() => {
              getTsgToken(access_token_decoded.tsg_id).then(setTimer, (err) => {
                __error('failed to renew user session after retry', err)
                // setTimer()
              })
            }, INTERVAL)
          })
        }
        return
      }
      setTimer()
    }, INTERVAL)
  }
  setTimer()
}

export function getFawkesTenantId(): string | undefined {
  const { auth } = stateRef.current
  const tenantId = auth?.fawkes_tenant_id ||
    sessionStorage.getItem('currentTenantId')
  return (tenantId !== 'null' && tenantId) || undefined
}

export function getRedirectURL() {
  const { auth, main } = stateRef.current
  const redirectUrl = main?.tenant?.cluster?.redirectURL || auth?.fawkes_paas_api_url ||
    main?.tenant?.info?.runtime_attributes?.paas_api_url ||
    auth?.instances?.get(FAWKES_APP_ID)?.runtime_attributes?.paas_api_url
  if (redirectUrl) {
    return redirectUrl
  }
  console.error('failed to get redirect url from state')
  return ''
}

export function getCustomServiceURL(serviceUrl: string) {
  let SERVICE_BASE_URL = getRedirectURL()
  if (!SERVICE_BASE_URL) return { SERVICE_URL: undefined }
  SERVICE_BASE_URL = SERVICE_BASE_URL.endsWith('/') ? SERVICE_BASE_URL.slice(0, SERVICE_BASE_URL.length - 1) : SERVICE_BASE_URL
  serviceUrl = serviceUrl.startsWith('/') ? serviceUrl.slice(1, serviceUrl.length) : serviceUrl
  return {
    SERVICE_URL: `${SERVICE_BASE_URL}/${serviceUrl}`
  }
}

export function getAdminServiceURL(adminUrl: string, extraParams?: Record<string, any>) {
  let urlParams = '?'
  if (!_.isEmpty(extraParams)) {
    for (const key in extraParams) {
      if (key && extraParams[key]) {
        urlParams = `${urlParams}&${key}=${extraParams[key]}`
      }
    }
  }
  urlParams = urlParams === '?' ? '' : urlParams
  return {
    LOGIN_SERVICE_URL: `${getAdminClusterURL()}/${adminUrl}${urlParams}`
  }
}

export function isInSession() {
  const tenantId = getFawkesTenantId()
  // see if we have token or not - with tenantId from url or from session storage
  const token = getSession(tenantId)
  return !tenantId && !token
}

export const getValidUserPreference = (userPreference: Record<string, any>) => {
  const preference = {} as Record<string, any>
  for (const key of Object.keys(userPreference)) {
    if (_.includes(validUserPreferenceAttributes, key)) {
      preference[key] = userPreference[key]
    }
  }
  return preference
}

export const checkIsFeatureEnabled = (featureList: FeatureInfo[] = [], featureKey = '') => {
  return featureList.some(feature => feature?.name === featureKey && feature.toggle === 1)
}

export function deepEquals(a: any, b: any, ca: any[] = [], cb: any[] = []): boolean {
  // Partially extracted from node-deeper and adapted to exclude comparison
  // checks for functions.
  // https://github.com/othiym23/node-deeper
  if (a === b) {
    return true
  }
  else if (typeof a === 'function' || typeof b === 'function') {
    // Assume all functions are equivalent
    // see https://github.com/mozilla-services/react-jsonschema-form/issues/255
    return true
  }
  else if (typeof a !== 'object' || typeof b !== 'object') {
    return false
  }
  else if (a === null || b === null) {
    return false
  }
  else if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }
  else if (a instanceof RegExp && b instanceof RegExp) {
    return (
      a.source === b.source &&
      a.global === b.global &&
      a.multiline === b.multiline &&
      a.lastIndex === b.lastIndex &&
      a.ignoreCase === b.ignoreCase
    )
  }
  else if (_.isArguments(a) || _.isArguments(b)) {
    if (!(_.isArguments(a) && _.isArguments(b))) {
      return false
    }
    const slice = Array.prototype.slice
    return deepEquals(slice.call(a), slice.call(b), ca, cb)
  }
  else {
    if (a.constructor !== b.constructor) {
      return false
    }

    const ka = Object.keys(a)
    const kb = Object.keys(b)
    // don't bother with stack acrobatics if there's nothing there
    if (ka.length === 0 && kb.length === 0) {
      return true
    }
    if (ka.length !== kb.length) {
      return false
    }

    let cal = ca.length
    while (cal--) {
      if (ca[cal] === a) {
        return cb[cal] === b
      }
    }
    ca.push(a)
    cb.push(b)

    ka.sort()
    kb.sort()
    for (let j = ka.length - 1; j >= 0; j--) {
      if (ka[j] !== kb[j]) {
        return false
      }
    }

    let key
    for (let k = ka.length - 1; k >= 0; k--) {
      key = ka[k]
      if (!deepEquals(a[key], b[key], ca, cb)) {
        return false
      }
    }

    ca.pop()
    cb.pop()

    return true
  }
}

export const findLeftNavTreeNodeWithKey = (routeData: any, key: any): any => {
  if (!routeData)
    return null

  let found = null
  for (let i = 0; i < routeData.length && !found; i++) {
    const node = routeData[i]
    if (key === node.key) {
      found = node
    }

    if (!found && node.children) {
      found = findLeftNavTreeNodeWithKey(node.children, key)
    }
  }
  return found
}

export function getRBAPermission(rbaPath: string) {
  const rba = _.get(stateRef.current, `main.loggedInUser.rba.${rbaPath}`) || 'disable'
  return rba
}

export function isPermissionEnabled(permission: string) {
  return permission === 'enable'
}

export function isPermissionDisabled(permission: string) {
  return permission === 'disable'
}

export function isPermissionReadOnly(permission: string) {
  return permission === 'read-only'
}

export function getLocationPermission(objLocation: string, configLocation: ConfigLocation) {
  //check if the object location same as global location
  // let globalLocation = getPathValue(getStoreState(), 'main.configLocation');
  const locationName = configLocation ? configLocation.container ?
    configLocation.container.name : null :
    null
  return locationName === objLocation
}

export const isValidRoute = (route: NavItem) => {
  // has a copy in RouteViewer, ensure in sync
  const { auth, main } = stateRef.current
  const { licenseInfo, featuresInfo } = main || {}
  const { instances, access, tsg_id } = auth || {}
  const configLocation = { container: {} } as ConfigLocation // for backward compatibility
  const objs = {
    ...configLocation,
    configLocation,
    location: history.location,
    licenseInfo,
    featuresInfo,
    instances,
    tsg_id,
    access,
    auth,
    main,
    navItem: route,
  }
  try {
    const isAvail = !route.avail || Boolean((route.avail as FawkesAvailFunc)(objs, licenseInfo, featuresInfo))
    return isAvail && Boolean(route.bypassGlobalAvail || globalAvail()) &&
      Boolean(route.bypassTsgAvail || tsgAccessAvail())
  }
  catch (e) {
    console.error('failed to validate route due to exception', route, e)
    return false
  }
}

export function getUserLoggedInInfo() {
  const jwToken = window.location.hash.split('#_rjwt=')[1]
  const now = Date.now()
  if (jwToken) {
    // it was a redirect
    const { sub, exp, iat, redirectURL, supportAccountId } = jwt_decode(jwToken)
    if (isTokenValid({ exp, iat }) && // allow a little ahead in local time
      sub === getParam('tenantId')) { // token is not expired, issued earlier & match the tenant
      window.location.hash = '' // reset hash
      saveSession({ sub, token: jwToken, exp })
      return {
        isLoggedIn: true,
        tenant: {
          supportAccountId,
          cluster: {
            redirectURL
          }
        }
      }
    }
    if (iat && now < iat * 1000) {
      console.error(`JWT issue date is later than browser local time about ${iat * 1000 - now} ms`)
      // if (isTokenValid({ exp, iat: Date.now() / 1000 })) {
      //   alert('You local time is different from the server time more than the tolerance. Please check your local time settings.')
      //   const diff = iat * 1000 - Date.now()
      //   if (diff <= JWT_IAT_TOLERANCE) {
      //     setTimeout(() => window.location.reload(), diff)
      //   }
      //   else {
      //     window.location.reload()
      //   }
      //   return
      // }
    }
    return {
      isLoggedIn: false,
      expired: true,
    }
  }
  else {
    // got here after a refresh of the page or first time by navigating or copy-pasting url.
    // tenantId from url or sessionId.
    //if not from URL, get from localstorage and save it in sessionstorage
    const tenantId = getParam('tenantId') || localStorage.getItem('currentTenantId') || undefined
    // see if we have token or not - with tenantId from url or from session storage
    const token = getSession(tenantId)
    if (token) {
      const { sub, exp, iat, redirectURL, supportAccountId } = jwt_decode(token)
      if (isTokenValid({ exp, iat })) {
        console.log('Sending the token, with T=', sub)
        saveSession({ sub, exp, token })
        return {
          isLoggedIn: true,
          tenant: {
            supportAccountId,
            cluster: {
              redirectURL
            }
          }
        }
      }
      // else if (iat && now < iat * 1000) {
      //   console.error(`JTW issue date is later than browser local time about ${iat * 1000 - now} ms`)
      // }
    }
    // no redirected token or token expired or invalid.
    console.log('noSession since No TOKEN NO TENANT-ID.')
    return {
      isLoggedIn: false,
      noSession: !tenantId && !token
    }
  }
}

export const isInManageRoute = (path: string) =>
  path.startsWith(MGMT_OVERVIEW_ROUTE) || path.startsWith(MGMT_MANAGE_ROUTE)

export function sendErrorToDatadog(err: Error): never {
  try {
    getDatadogRum()?.addError(err, {
      type: 'server_error',
    })
  }
  catch (e) {
  }
  throw err
}

export function fetchLoggedInUser(tsg_id: string | undefined) {
  const url = new URL('api/sso/v1/user', getRedirectURL())
  return dedupPromise(`${url}?tsgId=${tsg_id}`, () => fetch(url.toString(), {
    credentials: 'omit',
    mode: 'cors',
    method: 'GET',
    timeout: REQUEST_TIMEOUT,
    headers: getAuthHeaders()
  }).then(async (resp) => {
    if (resp.ok) {
      const userInfo = await resp.json()
      return userInfo
    }
    const data = await resp.text().then(tryParseErrorJson)
    const err = new Error(`${resp.status}: failed to fetch user and tenant info from fawkes`)
    Object.defineProperty(err, 'cause', { value: data })
    sendErrorToDatadog(err)
  }, sendErrorToDatadog), { keepPromiseIn: 30000 }) // keep 30s
}

export function fetchFawkesMainInfo<T = AnyState>({ signal, url, tsg_id, errorMsg }: { url: string, tsg_id?: string, signal?: AbortController['signal'], errorMsg: string }): Promise<T> {
  return dedupPromise(tsg_id ? `${url}?tsgId=${tsg_id}` : url, () => fetch(url, {
    credentials: 'omit',
    mode: 'cors',
    method: 'GET',
    signal,
    timeout: REQUEST_TIMEOUT,
    headers: getAuthHeaders()
  }).then(async (resp) => {
    if (resp.ok) {
      return resp.json() as Promise<T>
    }
    const data = await resp.text().then(tryParseErrorJson)
    const err = new Error(`${resp.status}: ${errorMsg}`)
    Object.defineProperty(err, 'cause', { value: data })
    sendErrorToDatadog(err)
  }, sendErrorToDatadog), { keepPromiseIn: 30000 }) // keep 30s
}

export function fetchTokens() {
  const url = new URL('api/ui/tokens', getAdminClusterURL()).toString()
  return fetchFawkesMainInfo<TokenInfo>({ url, errorMsg: 'failed to fetch tokens from fawkes' })
}

export function fetchFeatures({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/features', getAdminClusterURL()).toString()
  return fetchFawkesMainInfo<FeatureInfo[]>({ url, tsg_id, signal, errorMsg: 'failed to fetch features from fawkes' })
}

export function fetchFqdnInfo({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/extensions/gpcs/gpFQDN', getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to fetch FQDNs from fawkes' })
}

export function fetchLicenseInfo({ signal, tsg_id }: FetchPromiseOption) {
  const url = new URL('api/extensions/gpcs/license', getRedirectURL()).toString()
  return fetchFawkesMainInfo({ url, tsg_id, signal, errorMsg: 'failed to fetch license info from fawkes' })
}

export function getNotificationsPromise() {
  const { LOGIN_SERVICE_URL } = getAdminServiceURL(
    'api/v1/notifications', { readStatus: 'false' }
  )
  const promise = ajax({
    withCredentials: true,
    method: 'GET',
    responseType: 'json',
    url: LOGIN_SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}

export function getMigrationSatusPromise() {
  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    timeout: REQUEST_TIMEOUT,
    url: `${getRedirectURL()}api/migration/status`,
    headers: getAuthHeaders()
  }).toPromise().catch(sendErrorToDatadog)
  return promise
}

export function getSubTenantInfoPromise() {
  const { SERVICE_URL } = getCustomServiceURL(
    'api/extensions/gpcs/get/get_tenant_subtenant_info'
  )

  const promise = ajax({
    withCredentials: false,
    method: 'GET',
    responseType: 'json',
    url: SERVICE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  }).toPromise().catch(sendErrorToDatadog) as Promise<AjaxResponse>
  return promise
}
